/**
 * ImageTile — a picture an agent found, loaded only when the person asks.
 *
 * data: { url: string; caption?; alt? }
 *
 * Why click-to-load: an image URL the model wrote is an exfiltration
 * channel — the browser fetches it on render, and the URL can carry
 * whatever the model was tricked into putting in it (RW-4.48 g made chat
 * and markdown images links for the same reason). The tile shows where
 * the picture is from; one click fetches it.
 */
import * as React from "react";
import { ImageIcon } from "lucide-react";
import { hostOf, safeHttpUrl } from "@/lib/safe-url";
import type { TileBodyProps } from "../registry";

interface ImageData {
  url?: string;
  caption?: string;
  alt?: string;
}

export function ImageTile({ data }: TileBodyProps) {
  const d = data as ImageData;
  const url = safeHttpUrl(d.url);
  const [shown, setShown] = React.useState(false);
  if (!url) return <p className="text-[12.5px] text-fg-faint">No image.</p>;

  return (
    <div className="flex h-full flex-col gap-1.5">
      {shown ? (
        <img
          src={url}
          alt={d.alt ?? d.caption ?? ""}
          referrerPolicy="no-referrer"
          className="min-h-0 w-full flex-1 object-contain"
        />
      ) : (
        <button
          type="button"
          onClick={() => setShown(true)}
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 border border-dashed border-border bg-bg-sunken text-fg-muted transition hover:border-accent hover:text-fg"
        >
          <ImageIcon size={18} />
          <span className="text-[12px]">Show image</span>
          <span className="font-mono text-[10px] text-fg-faint">{hostOf(url)}</span>
        </button>
      )}
      {d.caption && <p className="flex-shrink-0 text-[11.5px] text-fg-muted">{d.caption}</p>}
    </div>
  );
}
